import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils/cn';
import { groupHoverFill, iconSize, restingFg, selectedSurface } from './styles';
import type { NavItem } from './types';

// Both shapes draw from the same tokens, so an icon never changes brightness
// or size when the sidebar collapses.
const pillActive = selectedSurface;
const pillInactive = `${restingFg} ${groupHoverFill}`;

export function StripItem({
  item,
  active,
  onNavigate,
}: {
  item: NavItem;
  active: boolean;
  onNavigate?: (() => void) | undefined;
}) {
  // The rail is icon-only, so the name lives in a tooltip — it is the only
  // thing identifying the icon.
  const label = item.shortLabel ?? item.label;
  const Icon = item.icon;
  const inner = (
    // The pill carries the colour and the icon inherits it — the same
    // arrangement the column's rows use, so the accent cannot end up applied at
    // one level here and another there.
    <span
      className={cn(
        'flex items-center justify-center size-9 min-[1600px]:size-10 rounded-lg transition-colors',
        active ? pillActive : pillInactive,
        item.accent && !active && 'text-brand-accent',
      )}
    >
      <FontAwesomeIcon icon={Icon} className={iconSize} />
    </span>
  );

  const cls = cn(
    'group flex justify-center w-full cursor-pointer',
    item.className,
  );

  const trigger: ReactElement = item.onClick ? (
    <button
      type='button'
      aria-label={label}
      onClick={() => {
        item.onClick?.();
        onNavigate?.();
      }}
      className={cls}
    />
  ) : (
    <Link
      to={item.href as never}
      aria-label={label}
      onClick={onNavigate}
      className={cls}
    />
  );

  return (
    <Tooltip>
      <TooltipTrigger render={trigger}>{inner}</TooltipTrigger>
      <TooltipContent side='right'>{label}</TooltipContent>
    </Tooltip>
  );
}
